// snow.js — winter weather for Bare / Winter mode (leafType === 'none').
//
// Two parts, both gated on winter mode and coordinated by updateWinter(), which
// main.js calls once per frame from animate():
//   1. A scene-wide snowfall — a single THREE.Points system whose flakes drift
//      down and recycle to the top, so it snows forever without spawning meshes.
//   2. The static-garden frosting, which lives in environment.js (it owns the pot
//      / rock / gravel refs); we just flip it on and off in step with the snow.
//
// Per-branch tree snow is separate — it's built in tree.js alongside the foliage
// (see Branch.createSnow) so it rides the existing prune machinery.

import * as THREE from 'three';
import { app, parameters } from './core.js';
import { setEnvironmentSnow } from './environment.js';

const COUNT = 1500;        // flakes in the air at once
const AREA = 700;          // half-extent of the snowfall box in x/z
const TOP = 420;           // spawn/recycle height
const BOTTOM = -30;        // ground level: a flake below this wraps to the top

const FALL_OPACITY = 0.9;  // material opacity of the snowfall at full winter
const FALL_FADE_S = 3.0;   // seconds the airborne flakes take to fade out at winter's end
                           // (matches the tree/garden melt so everything clears together)

let points = null;         // the THREE.Points snowfall
let baseX = null, baseZ = null, vel = null, phase = null; // per-flake state
let built = false;         // snowfall lazily built on first winter
let active = false;        // current winter state (mirrors leafType === 'none')
let lastT = 0;             // timeSeconds at the previous advance()
let fadingOut = false;     // winter ended but flakes are still falling + fading out
let _fallFade = null;      // the active opacity-fade tween (killed if winter re-enables)

// Soft round flake sprite: a white radial gradient fading to transparent.
let _flakeTex = null;
function flakeTexture() {
  if (_flakeTex) return _flakeTex;
  const c = document.createElement('canvas');
  c.width = c.height = 32;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  g.addColorStop(0.0, 'rgba(255,255,255,1)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.85)');
  g.addColorStop(1.0, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  _flakeTex = new THREE.CanvasTexture(c);
  return _flakeTex;
}

function buildSnowfall() {
  const positions = new Float32Array(COUNT * 3);
  baseX = new Float32Array(COUNT);
  baseZ = new Float32Array(COUNT);
  vel = new Float32Array(COUNT);
  phase = new Float32Array(COUNT);

  for (let i = 0; i < COUNT; i++) {
    const x = (Math.random() - 0.5) * 2 * AREA;
    const z = (Math.random() - 0.5) * 2 * AREA;
    baseX[i] = x;
    baseZ[i] = z;
    vel[i] = 16 + Math.random() * 26;          // fall speed, units/sec
    phase[i] = Math.random() * Math.PI * 2;     // sway offset so flakes desync
    positions[i * 3] = x;
    positions[i * 3 + 1] = BOTTOM + Math.random() * (TOP - BOTTOM); // pre-fill the column
    positions[i * 3 + 2] = z;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const mat = new THREE.PointsMaterial({
    map: flakeTexture(),
    size: 5,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0,          // starts invisible; updateWinter fades it in to FALL_OPACITY
    depthWrite: false,   // flakes shouldn't occlude each other or punch holes
  });

  points = new THREE.Points(geo, mat);
  points.frustumCulled = false; // the box is bigger than any single flake's bounds
  points.renderOrder = 2;
  app.scene.add(points);
}

// Drift every flake down a frame; recycle any that reach the ground back to the
// top. Horizontal position is base + a gentle sway (so flakes wander without
// permanently drifting out of the box).
function advance(timeSeconds) {
  const dt = lastT ? Math.min(timeSeconds - lastT, 0.1) : 0;
  lastT = timeSeconds;
  if (!dt) return;

  const arr = points.geometry.attributes.position.array;
  for (let i = 0; i < COUNT; i++) {
    const i3 = i * 3;
    let y = arr[i3 + 1] - vel[i] * dt;
    if (y < BOTTOM) {
      // Respawn at the top in a fresh column.
      y = TOP;
      baseX[i] = (Math.random() - 0.5) * 2 * AREA;
      baseZ[i] = (Math.random() - 0.5) * 2 * AREA;
    }
    arr[i3] = baseX[i] + Math.sin(timeSeconds * 0.6 + phase[i]) * 12;
    arr[i3 + 1] = y;
    arr[i3 + 2] = baseZ[i] + Math.cos(timeSeconds * 0.45 + phase[i]) * 10;
  }
  points.geometry.attributes.position.needsUpdate = true;
}

// Called once at boot (after the initial tree exists, before the animate loop) to
// match the winter scene to the starting leafType WITHOUT the entrance animation —
// a freshly generated/loaded winter tree shows its full snow at once (the branch
// snow is likewise built full). Later leafType changes animate via updateWinter.
export function initWinter(timeSeconds) {
  active = parameters.leafType === 'none' || parameters.leafType === 'evergreen';
  lastT = timeSeconds;
  if (active) {
    if (!built) { buildSnowfall(); built = true; }
    points.visible = true;
    points.material.opacity = FALL_OPACITY; // snap to full, no fade-in
  }
  setEnvironmentSnow(active, false);         // snap the garden frost, no accumulate
}

// Called every frame from animate(). Flips the snowfall + garden frost on/off in
// step with Bare / Winter mode, then advances the falling snow while it's active.
export function updateWinter(timeSeconds) {
  // Both the bare 'none' tree and the snowy 'evergreen' conifer get the winter scene
  // (falling snow + garden frost) and the per-branch snow shed/regrow updates.
  const want = parameters.leafType === 'none' || parameters.leafType === 'evergreen';
  if (want !== active) {
    active = want;
    if (want && !built) { buildSnowfall(); built = true; }
    setEnvironmentSnow(want);
    if (want) {
      lastT = timeSeconds; // avoid a big first-frame dt after a long pause
      // Entering winter: fade the snowfall in (from wherever a prior fade-out left
      // it) so it starts snowing gradually instead of popping on full.
      if (points) {
        if (_fallFade) _fallFade.kill();
        fadingOut = false;
        points.visible = true;
        _fallFade = gsap.to(points.material, {
          opacity: FALL_OPACITY, duration: FALL_FADE_S, ease: 'power1.out',
          onComplete: () => { _fallFade = null; }
        });
      }
    } else if (points && points.visible) {
      // Winter ending: keep the flakes falling but fade them out, then hide them —
      // so the airborne snow thins away in step with the melting frost rather than
      // blinking off. The advance loop below keeps running while fadingOut.
      fadingOut = true;
      lastT = timeSeconds;
      if (_fallFade) _fallFade.kill();
      _fallFade = gsap.to(points.material, {
        opacity: 0, duration: FALL_FADE_S, ease: 'power1.in',
        onComplete: () => { points.visible = false; fadingOut = false; _fallFade = null; }
      });
    }
  }
  // Drive the flakes while it's winter, or while they're still falling + fading out.
  if (points && (active || fadingOut)) {
    // dt captured before advance() (which updates lastT) so the tree's snow
    // shed/regrow steps on the same delta.
    const dt = lastT ? Math.min(timeSeconds - lastT, 0.1) : 0;
    advance(timeSeconds);
    if (active && app.tree) app.tree.updateSnow(dt);
  }
}
